import React from 'react';
import {WithTranslation} from 'react-i18next';
import {Platform, ScrollView, View} from 'react-native';
import {noop} from 'lodash';

import LoadingIndicator from '/component/base/LoadingIndicator';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import NavigationHeader from '/component/layout/NavigationHeader';
import AccountSettingSection from '/component/partial/setting/AccountSettingSection';
import {Settings} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {generateComponentCallback} from '/util/component.util';

import {styles} from './AccountSettings.style';

interface AccountSettingsProps extends WithTranslation {
  tt: (key: string, defaultValue: string) => string;
  email: string;
  phone: string;
  hasProfile: boolean;
  getProfile: () => void;
}

class AccountSettings extends React.PureComponent<AccountSettingsProps> {
  override componentDidMount() {
    const {getProfile = noop, hasProfile = false} = this.props;

    if (!hasProfile) {
      getProfile();
    }
  }

  override render() {
    const {
      tt = (key: string, defaultVal: string) => defaultVal,
      hasProfile = false,
      email = '',
      phone = '',
    } = this.props;

    const content = (
      <NavigationHeader
        style={
          Platform.OS === 'web' &&
          (styles as Record<string, object>).headerContainer
            ? {
                header: (styles as Record<string, object>)
                  .headerContainer as object,
              }
            : {}
        }>
        <View style={styles.base}>
          <View style={styles.header}>
            <Gutter>
              <Text
                accessible={true}
                accessibilityRole="header"
                testID={Settings.accountSettingsTitle}
                style={styles.heading}
                variant="large">
                {tt('title', 'Account Settings')}
              </Text>
              {tt(
                'description',
                generateComponentCallback({
                  component: (
                    <Text
                      accessible={true}
                      accessibilityRole="text"
                      testID={Settings.accountSettingsDescription}
                      variant="small"
                      emphasis="low">
                      Here you can change the information associated with your
                      <Text variant="heading" emphasis="low">
                        {' OPTA'}
                      </Text>
                      VIA account. To change your public profile information
                      visit your profile in the app.
                    </Text>
                  ),
                }),
              )}
            </Gutter>
          </View>

          <ScrollView>
            {hasProfile ? (
              <View style={styles.content}>
                <AccountSettingSection
                  testID={Settings.accountSettingsUsername}
                  defaultValue={email}
                  title={tt('section.username.label', 'Username')}
                  description={tt(
                    'section.username.description',
                    'The email you’ll use to login.',
                  )}
                />
                <AccountSettingSection
                  testID={Settings.accountSettingsPhone}
                  defaultValue={phone}
                  title={tt('section.phone.label', 'Phone Number')}
                  description={''} // HIDDEN: CONNAPP-441
                />
              </View>
            ) : (
              <View style={styles.loading}>
                <LoadingIndicator />
              </View>
            )}
          </ScrollView>
        </View>
      </NavigationHeader>
    );

    if (
      Platform.OS === 'web' &&
      (styles as Record<string, object>).screenWrapper
    ) {
      return (
        <View
          style={(styles as Record<string, object>).screenWrapper as object}>
          {content}
        </View>
      );
    }
    return content;
  }
}

export default withTranslation('screen.AccountSettings')(AccountSettings);
